/**
 * Organization Form Component
 * Create and edit business (organization) records.
 *
 * Section order follows the business page: Basic Information -> Address -> Business / Billing.
 * - Organization ID is server-generated when omitted (this form never sends one) and cannot be changed once set.
 * - Territory Numeric Designation is text, so leading zeros are kept ("04").
 * - "Same as physical address" is saved with the record.
 * - Required fields are conditional (utils/organizationRequiredFields.ts, mirrored by the database): a new record
 *   needs them all; an existing record that was already missing some still saves unrelated changes.
 * - On create, possible duplicates are listed as you type (paste is allowed); creating anyway needs a tick.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  createOrganization,
  updateOrganization,
  checkOrgFieldUniqueness,
  findFacilityIdConflict,
  facilityIdConflictMessage,
  findOrganizationDuplicateCandidates,
} from "../../services/organizations.service";
import type { DuplicateCandidate } from "../../services/organizations.service";
import { QUERY_KEYS } from "../../utils/constants";
import type {
  Organization,
  OrganizationTypeTag,
} from "../../types/organization.types";
import { FACILITY_TYPE_PICKER_OPTIONS } from "../../types/organization.types";
import type { UUID } from "../../types/common.types";
import Button from "../common/Button";
import Input from "../common/Input";
import { US_STATES } from "../../types/event.types";
import {
  REQUIRED_FIELD_LABELS,
  describeOrganizationSaveError,
  getBlockingRequiredOrgFields,
  getMissingRequiredOrgFields,
  isFacilityKind,
} from "../../utils/organizationRequiredFields";
import type {
  RequiredFieldInput,
  RequiredOrgField,
} from "../../utils/organizationRequiredFields";

// Validation schema (format rules; conditional requiredness is applied in onSubmit)
const organizationSchema = z.object({
  name: z.string().trim().min(1, "Business name is required"),
  legal_name: z.string().optional(),
  description: z.string().optional(),
  email: z.string().email("Invalid email format").optional().or(z.literal("")),
  phone: z.string().optional(),
  website: z.string().url("Invalid URL format").optional().or(z.literal("")),
  facility_id: z.string().optional(),
  zuora_link: z.string().optional(),
  account_number: z.string().optional(),
  territory_designation: z
    .string()
    .trim()
    .regex(/^\d{0,9}$/, "Digits only (up to 9); leading zeros are kept")
    .optional(),
  region: z.string().optional(),
  affiliate_id: z.string().optional(),
  facility_type_key: z.string().min(1, "Facility type is required"),
  is_prospect: z.boolean().optional(),
  shooting_qualification: z.boolean().optional(),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

interface AddressFormData {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface OrganizationFormProps {
  organization?: Organization;
  /** receives the created organization on create (slice 2: "Business + Vendor" continues to vendor details) */
  onSuccess?: (created?: Organization) => void;
  onCancel?: () => void;
}

/** Picker key for a stored organization_type ("Retailer with Range" legacy strings included). */
export const facilityTypeKeyFor = (stored?: string | null): string => {
  if (!stored) {
    return "";
  }
  const norm = stored.trim().toLowerCase().replace(/\s+/g, "_");
  return FACILITY_TYPE_PICKER_OPTIONS.some((o) => o.key === norm) ? norm : "";
};

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

const Req: React.FC<{ show: boolean }> = ({ show }) =>
  show ? <span className="text-red-500"> *</span> : null;

const REASON_LABELS: Record<string, string> = {
  name: "name",
  email: "email",
  phone: "phone",
  facility_id: "Facility ID",
  affiliate_id: "Affiliate ID",
  account_number: "account number",
};

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const [organizationTypes, setOrganizationTypes] = useState<
    OrganizationTypeTag[]
  >(organization?.organization_types || []);
  const [primaryAddress, setPrimaryAddress] = useState<AddressFormData>({
    line1: organization?.address_line1 || "",
    line2: organization?.address_line2 || "",
    city: organization?.address_city || "",
    state: organization?.address_state || "",
    postalCode: organization?.address_postal_code || "",
    country: organization?.address_country || "USA",
  });
  const [mailingAddress, setMailingAddress] = useState<AddressFormData>({
    line1: organization?.mailing_address_line1 || "",
    line2: organization?.mailing_address_line2 || "",
    city: organization?.mailing_city || "",
    state: organization?.mailing_state || "",
    postalCode: organization?.mailing_postal_code || "",
    country: organization?.mailing_country || "USA",
  });
  const [sameAsPhysical, setSameAsPhysical] = useState<boolean>(
    organization?.mailing_same_as_physical === true,
  );
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [requiredErrors, setRequiredErrors] = useState<RequiredOrgField[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [createAnyway, setCreateAnyway] = useState(false);
  const isEditMode = !!organization;

  // Sync organizationTypes when organization prop changes
  useEffect(() => {
    if (organization?.organization_types) {
      setOrganizationTypes(organization.organization_types);
    }
  }, [organization?.organization_types]);

  // Slice 2: an existing organization with no facility type (a vendor-only record, or a legacy row) can be edited
  // without being forced to pick one; a new organization still needs one (vendor-only ones are created separately).
  const allowNoType =
    isEditMode && !(organization?.organization_type || "").trim();
  const formSchema = React.useMemo(
    () =>
      allowNoType
        ? organizationSchema.extend({ facility_type_key: z.string() })
        : organizationSchema,
    [allowNoType],
  );
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OrganizationFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: organization?.name || "",
      legal_name: organization?.legal_name || "",
      description: organization?.description || "",
      email: organization?.email || "",
      phone: organization?.phone || "",
      website: organization?.website || "",
      facility_id: organization?.facility_id || "",
      zuora_link: organization?.zuora_link || "",
      account_number: organization?.account_number || "",
      territory_designation: organization?.territory_designation ?? "",
      region: organization?.region || "",
      affiliate_id: organization?.affiliate_id || "",
      facility_type_key: facilityTypeKeyFor(organization?.organization_type),
      is_prospect: organization?.is_prospect || false,
      shooting_qualification: organization?.shooting_qualification || false,
    },
  });

  // Keep mailing in step with physical while "same as physical" is ticked (the database does the same on save)
  useEffect(() => {
    if (sameAsPhysical) {
      setMailingAddress(primaryAddress);
    }
  }, [sameAsPhysical, primaryAddress]);

  const w = watch();
  const pickedType = FACILITY_TYPE_PICKER_OPTIONS.find(
    (o) => o.key === w.facility_type_key,
  )?.value;
  const idsRequired = isFacilityKind(pickedType) && w.is_prospect !== true;

  // The "before" image for edit mode: fields a legacy record was already missing never block an unrelated save.
  const before: RequiredFieldInput | null = organization
    ? {
        name: organization.name,
        email: organization.email,
        phone: organization.phone,
        address_line1: organization.address_line1,
        address_city: organization.address_city,
        address_state: organization.address_state,
        address_postal_code: organization.address_postal_code,
        facility_id: organization.facility_id,
        affiliate_id: organization.affiliate_id,
        organization_type: organization.organization_type,
        is_prospect: organization.is_prospect,
      }
    : null;
  const legacyMissing = useMemo(
    () => (before ? getMissingRequiredOrgFields(before) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization?.id, organization?.updated_at],
  );

  // ---- duplicate candidates (create mode only, debounced)
  const dupKey = isEditMode
    ? ""
    : JSON.stringify([
        w.name,
        w.legal_name,
        w.email,
        w.phone,
        w.facility_id,
        w.affiliate_id,
        w.account_number,
      ]);
  useEffect(() => {
    if (isEditMode) {
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const found = await findOrganizationDuplicateCandidates({
          name: w.name,
          legal_name: w.legal_name,
          email: w.email,
          phone: w.phone,
          facility_id: w.facility_id,
          affiliate_id: w.affiliate_id,
          account_number: w.account_number,
        });
        if (!cancelled) {
          setCandidates(found);
          setCreateAnyway(false);
        }
      } catch (e) {
        // Warn-only feature: a failed lookup must never block creating an organization.
        console.warn("[OrganizationForm] duplicate lookup failed", e);
        if (!cancelled) {
          setCandidates([]);
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dupKey, isEditMode]);

  const createMutation = useMutation({
    mutationFn: createOrganization,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      onSuccess?.(created);
    },
    onError: (error: Error) => {
      setSaveError(describeOrganizationSaveError(error.message));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: UUID;
      updates: Partial<Organization>;
    }) => updateOrganization(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      queryClient.invalidateQueries({
        queryKey: ["organization", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization-stats", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization-status-history", variables.id],
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("[OrganizationForm] Update error:", error);
      setSaveError(describeOrganizationSaveError(error.message));
    },
  });

  const onSubmit = async (data: OrganizationFormData) => {
    setDuplicateError(null);
    setSaveError(null);

    const organizationType = FACILITY_TYPE_PICKER_OPTIONS.find(
      (o) => o.key === data.facility_type_key,
    )?.value;

    // Conditional required fields
    const after: RequiredFieldInput = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      address_line1: primaryAddress.line1,
      address_city: primaryAddress.city,
      address_state: primaryAddress.state,
      address_postal_code: primaryAddress.postalCode,
      facility_id: data.facility_id,
      affiliate_id: data.affiliate_id,
      organization_type: organizationType,
      is_prospect: data.is_prospect,
    };
    const blocking = getBlockingRequiredOrgFields(after, before);
    setRequiredErrors(blocking);
    if (blocking.length > 0) {
      return;
    }

    if (!isEditMode && candidates.length > 0 && !createAnyway) {
      setDuplicateError(
        "Possible duplicates were found. Open them to check, or tick “None of these is the same business” to create anyway.",
      );
      return;
    }

    // Check uniqueness of affiliate_id and facility_id
    const excludeId = isEditMode ? organization?.id : undefined;
    // Facility ID (migration 996): checked only when it is introduced or changed, so an existing duplicate (left
    // for the business owner to resolve) keeps saving its other fields. Covers other organizations AND other
    // organizations' locations; the organization's own locations may share it.
    const facilityChanged =
      (data.facility_id ?? "").trim() !==
      (isEditMode ? (organization?.facility_id ?? "").trim() : "");
    const [affidConflict, facilityConflict] = await Promise.all([
      checkOrgFieldUniqueness(
        "affiliate_id",
        data.affiliate_id || "",
        excludeId,
      ),
      facilityChanged
        ? findFacilityIdConflict(data.facility_id || "", excludeId)
        : Promise.resolve(null),
    ]);
    if (affidConflict || facilityConflict) {
      const parts: string[] = [];
      if (affidConflict) {
        parts.push(
          `Affiliate ID "${data.affiliate_id}" is already used by "${affidConflict}"`,
        );
      }
      if (facilityConflict) {
        parts.push(
          facilityIdConflictMessage(data.facility_id || "", facilityConflict),
        );
      }
      const msg = parts.join(". ");
      setDuplicateError(msg.endsWith(".") ? msg : msg + ".");
      return;
    }

    const territory = (data.territory_designation ?? "").trim();
    const organizationData: Partial<Organization> = {
      name: data.name.trim(),
      legal_name: data.legal_name || undefined,
      description: data.description || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      website: data.website || undefined,
      facility_id: data.facility_id || undefined,
      zuora_link: data.zuora_link || undefined,
      account_number: data.account_number || undefined,
      // Text, leading zeros kept. Clearing it on an existing record really clears it (the DB syncs the legacy integer).
      territory_designation: territory
        ? territory
        : isEditMode && organization?.territory_designation
          ? null
          : undefined,
      region: data.region || undefined,
      affiliate_id: data.affiliate_id || undefined,
      organization_type: organizationType,
      is_prospect: data.is_prospect,
      shooting_qualification: data.shooting_qualification,
      // Address fields
      address_line1: primaryAddress.line1 || undefined,
      address_line2: primaryAddress.line2 || undefined,
      address_city: primaryAddress.city || undefined,
      address_state: primaryAddress.state?.toUpperCase() || undefined,
      address_postal_code: primaryAddress.postalCode || undefined,
      address_country: primaryAddress.country || undefined,
      mailing_same_as_physical: sameAsPhysical,
      mailing_address_line1: mailingAddress.line1 || undefined,
      mailing_address_line2: mailingAddress.line2 || undefined,
      mailing_city: mailingAddress.city || undefined,
      mailing_state: mailingAddress.state?.toUpperCase() || undefined,
      mailing_postal_code: mailingAddress.postalCode || undefined,
      mailing_country: mailingAddress.country || undefined,
      // Legacy array field (kept for backwards compatibility)
      organization_types: organizationTypes,
    };
    // organization_id is NOT sent: the database generates it on create and it is read-only afterwards.

    // Remove undefined values to avoid sending nulls for missing columns
    const cleanedData = Object.fromEntries(
      Object.entries(organizationData).filter(([, v]) => v !== undefined),
    ) as Partial<Organization>;

    if (isEditMode && organization) {
      updateMutation.mutate({ id: organization.id, updates: cleanedData });
    } else {
      createMutation.mutate(
        cleanedData as Omit<Organization, "id" | "created_at" | "updated_at">,
      );
    }
  };

  const handlePrimaryAddressChange = (
    field: keyof AddressFormData,
    value: string,
  ) => {
    setPrimaryAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleMailingAddressChange = (
    field: keyof AddressFormData,
    value: string,
  ) => {
    setMailingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const reqErr = (f: RequiredOrgField) =>
    requiredErrors.includes(f) ? (
      <p
        className="mt-1 text-sm text-red-600"
        data-testid={`required-error-${f}`}
      >
        {REQUIRED_FIELD_LABELS[f]} is required
      </p>
    ) : null;
  // Asterisk: always on a new record; on an existing record only where it is filled (it cannot be cleared) or
  // where this save would newly require it.
  const star = (f: RequiredOrgField, applies = true) =>
    applies && (!isEditMode || !legacyMissing.includes(f));

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
      data-testid="organization-form"
    >
      {isEditMode && legacyMissing.length > 0 && (
        <div
          role="status"
          data-testid="legacy-missing-notice"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          This organization is missing:{" "}
          <strong>
            {legacyMissing.map((f) => REQUIRED_FIELD_LABELS[f]).join(", ")}
          </strong>
          . You can still save other changes; fill these in before activating
          it.
        </div>
      )}

      {/* 1. Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="org-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Name <span className="text-red-500">*</span>
            </label>
            <input id="org-name" {...register("name")} className={inputClass} />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="org-legal-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Legal Name
            </label>
            <input
              id="org-legal-name"
              {...register("legal_name")}
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="org-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
              <Req show={star("email")} />
            </label>
            <input
              id="org-email"
              type="email"
              {...register("email")}
              className={inputClass}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
            {reqErr("email")}
          </div>

          <div>
            <label
              htmlFor="org-phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone
              <Req show={star("phone")} />
            </label>
            <input
              id="org-phone"
              type="tel"
              {...register("phone")}
              className={inputClass}
            />
            {reqErr("phone")}
          </div>

          <div>
            <label
              htmlFor="org-website"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website
            </label>
            <input
              id="org-website"
              type="url"
              {...register("website")}
              placeholder="https://example.com"
              className={inputClass}
            />
            {errors.website && (
              <p className="mt-1 text-sm text-red-600">
                {errors.website.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="org-facility-id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Facility ID
              <Req show={star("facility_id", idsRequired)} />
            </label>
            <input
              id="org-facility-id"
              {...register("facility_id")}
              placeholder="From the Admin Portal address, after “FID=”"
              className={inputClass}
            />
            {reqErr("facility_id")}
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Organization ID
            </span>
            <div
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700"
              data-testid="org-id-readonly"
            >
              {organization?.organization_id ||
                (isEditMode
                  ? "Not assigned"
                  : "Assigned automatically on save")}
            </div>
          </div>

          <div>
            <label
              htmlFor="org-type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Facility Type{" "}
              {!allowNoType && <span className="text-red-500">*</span>}
            </label>
            <select
              id="org-type"
              {...register("facility_type_key")}
              className={`${inputClass} ${errors.facility_type_key ? "border-red-500" : ""}`}
            >
              <option value="">
                {allowNoType
                  ? "No facility type (vendor only)"
                  : "Select type..."}
              </option>
              {FACILITY_TYPE_PICKER_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.facility_type_key && (
              <p className="mt-1 text-sm text-red-600">
                {errors.facility_type_key.message}
              </p>
            )}
            {idsRequired && (
              <p className="mt-1 text-xs text-gray-500">
                This type needs a Facility ID and an Affiliate ID (not while it
                is a prospect).
              </p>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              {...register("is_prospect")}
              id="is_prospect"
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label
              htmlFor="is_prospect"
              className="text-sm font-medium text-gray-700"
            >
              Prospect
            </label>
            <span className="text-xs text-gray-500">
              (Check if this organization is a prospect)
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              {...register("shooting_qualification")}
              id="shooting_qualification"
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label
              htmlFor="shooting_qualification"
              className="text-sm font-medium text-gray-700"
            >
              Shooting Qualification
            </label>
            <span className="text-xs text-gray-500">
              (Check if this organization offers shooting qualification)
            </span>
          </div>
        </div>

        <div>
          <label
            htmlFor="org-description"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Description
          </label>
          <textarea
            id="org-description"
            {...register("description")}
            rows={3}
            className={inputClass}
          />
        </div>
      </div>

      {!isEditMode && candidates.length > 0 && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="duplicate-candidates"
          role="status"
        >
          <p className="font-medium">
            Possible duplicates ({candidates.length}). Check before creating a
            new record:
          </p>
          <ul className="mt-2 space-y-1">
            {candidates.map((c) => (
              <li key={c.id} data-testid="duplicate-candidate">
                <Link
                  to={`/organizations/${c.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline"
                >
                  {c.name}
                </Link>
                {[c.address_city, c.address_state].filter(Boolean).length >
                  0 && (
                  <span className="text-gray-600">
                    {" "}
                    ·{" "}
                    {[c.address_city, c.address_state]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                )}
                {c.is_archived && (
                  <span className="text-gray-600"> · disabled</span>
                )}
                <span className="text-gray-600">
                  {" "}
                  · same{" "}
                  {c.match_reasons.map((r) => REASON_LABELS[r] ?? r).join(", ")}
                </span>
              </li>
            ))}
          </ul>
          <label className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={createAnyway}
              onChange={(e) => setCreateAnyway(e.target.checked)}
              data-testid="create-anyway"
              className="h-4 w-4"
            />
            <span>None of these is the same business — create anyway</span>
          </label>
        </div>
      )}

      {/* 2. Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Physical Address</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Input
              label={`Address Line 1${star("address_line1") ? " *" : ""}`}
              value={primaryAddress.line1}
              onChange={(e) =>
                handlePrimaryAddressChange("line1", e.target.value)
              }
            />
            {reqErr("address_line1")}
          </div>
          <Input
            label="Address Line 2"
            value={primaryAddress.line2}
            onChange={(e) =>
              handlePrimaryAddressChange("line2", e.target.value)
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                label={`City${star("address_city") ? " *" : ""}`}
                value={primaryAddress.city}
                onChange={(e) =>
                  handlePrimaryAddressChange("city", e.target.value)
                }
              />
              {reqErr("address_city")}
            </div>
            <div>
              <label
                htmlFor="org-state"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                State
                <Req show={star("address_state")} />
              </label>
              <select
                id="org-state"
                value={primaryAddress.state}
                onChange={(e) =>
                  handlePrimaryAddressChange("state", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
              {reqErr("address_state")}
            </div>
            <div>
              <Input
                label={`Postal Code${star("address_postal_code") ? " *" : ""}`}
                value={primaryAddress.postalCode}
                onChange={(e) =>
                  handlePrimaryAddressChange("postalCode", e.target.value)
                }
              />
              {reqErr("address_postal_code")}
            </div>
          </div>
          <Input
            label="Country"
            value={primaryAddress.country}
            onChange={(e) =>
              handlePrimaryAddressChange("country", e.target.value)
            }
          />
        </div>
      </div>

      {/* Mailing Address */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Mailing Address</h3>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={sameAsPhysical}
              onChange={(e) => setSameAsPhysical(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded"
              data-testid="same-as-physical"
            />
            <span className="text-sm text-gray-700">
              Same as physical address
            </span>
          </label>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Address Line 1"
            value={mailingAddress.line1}
            onChange={(e) =>
              handleMailingAddressChange("line1", e.target.value)
            }
            disabled={sameAsPhysical}
          />
          <Input
            label="Address Line 2"
            value={mailingAddress.line2}
            onChange={(e) =>
              handleMailingAddressChange("line2", e.target.value)
            }
            disabled={sameAsPhysical}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              value={mailingAddress.city}
              onChange={(e) =>
                handleMailingAddressChange("city", e.target.value)
              }
              disabled={sameAsPhysical}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={mailingAddress.state}
                onChange={(e) =>
                  handleMailingAddressChange("state", e.target.value)
                }
                disabled={sameAsPhysical}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Postal Code"
              value={mailingAddress.postalCode}
              onChange={(e) =>
                handleMailingAddressChange("postalCode", e.target.value)
              }
              disabled={sameAsPhysical}
            />
          </div>
          <Input
            label="Country"
            value={mailingAddress.country}
            onChange={(e) =>
              handleMailingAddressChange("country", e.target.value)
            }
            disabled={sameAsPhysical}
          />
        </div>
      </div>

      {/* 3. Business / Billing Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">
          Business / Billing Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="org-account-number"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Account Number
            </label>
            <input
              id="org-account-number"
              {...register("account_number")}
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="org-territory"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Territory Numeric Designation
            </label>
            <input
              id="org-territory"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={9}
              {...register("territory_designation")}
              placeholder="e.g. 04"
              className={inputClass}
            />
            {errors.territory_designation && (
              <p className="mt-1 text-sm text-red-600">
                {errors.territory_designation.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="org-region"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Region
            </label>
            <input
              id="org-region"
              {...register("region")}
              className={inputClass}
            />
          </div>

          <div>
            <label
              htmlFor="org-affiliate-id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Affiliate ID
              <Req show={star("affiliate_id", idsRequired)} />
            </label>
            <input
              id="org-affiliate-id"
              {...register("affiliate_id")}
              placeholder="From Admin Portal Facility Details"
              className={inputClass}
            />
            {reqErr("affiliate_id")}
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="org-zuora"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Zuora Link
            </label>
            <input
              id="org-zuora"
              {...register("zuora_link")}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {requiredErrors.length > 0 && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
          data-testid="required-summary"
          role="alert"
        >
          Please fill in:{" "}
          {requiredErrors.map((f) => REQUIRED_FIELD_LABELS[f]).join(", ")}.
        </div>
      )}

      {duplicateError && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
          role="alert"
        >
          {duplicateError}
        </div>
      )}

      {saveError && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
          data-testid="save-error"
          role="alert"
        >
          {saveError}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Saving..."
            : isEditMode
              ? "Update Organization"
              : "Create Organization"}
        </Button>
      </div>
    </form>
  );
};

export default OrganizationForm;
